/**
 * Queue Position Velocity Tracker
 *
 * Tracks queue position changes over time for open orders.
 * Detects sudden movement (velocity spikes) indicating aggressive buying/selling.
 *
 * Ring buffer stores last 30 snapshots per order (at 5s interval = 2.5 min history).
 *
 * ## Time domain (#850)
 *
 * Snapshots carry SESSION-ELAPSED milliseconds, not an epoch. Contracts-per-
 * second is a measured rate, and subtracting two adjustable wall-clock stamps
 * to produce it lets a clock correction alone reclassify a stampede as a
 * stroll (a forward step) or erase the reading entirely (a backward step).
 *
 * The tracker deliberately reads no clock of its own: `record` takes the
 * elapsed value, so a caller cannot half-migrate one call site and leave the
 * rest defaulting to a different domain. Every sample for one order must come
 * from the same clock — see `src/lib/sessionElapsedClock.ts`, which also owns
 * the discontinuity signal telling a caller when to drop measured history.
 */

export interface QueueSnapshot {
  position: number;
  /** Session-elapsed ms (`sessionElapsedClock`), NOT an epoch. Comparable
   *  only with other snapshots recorded from the same elapsed generation. */
  elapsedMs: number;
}

export interface VelocityResult {
  /** Contracts per second moving toward front of queue (positive = moving up) */
  velocity: number;
  /** Direction: 'advancing' (toward front), 'retreating' (away), or 'stable' */
  direction: 'advancing' | 'retreating' | 'stable';
}

export type VelocitySeverity = 'normal' | 'warn' | 'alert';

const DEFAULT_RING_SIZE = 30;
export const DEFAULT_VELOCITY_WARN = 5; // contracts/sec
export const DEFAULT_VELOCITY_ALERT = 20; // contracts/sec

export interface QueueVelocityConfig {
  /** Max snapshots per order (default 30) */
  ringSize?: number;
  /** Velocity threshold for "warn" severity (contracts/sec, default 5) */
  velocityWarn?: number;
  /** Velocity threshold for "alert" severity (contracts/sec, default 20) */
  velocityAlert?: number;
}

export class QueueVelocityTracker {
  private rings = new Map<string, QueueSnapshot[]>();
  private ringSize: number;
  private velocityWarn: number;
  private velocityAlert: number;
  /** Orders with muted velocity alerts */
  private muted = new Set<string>();

  constructor(config?: QueueVelocityConfig) {
    this.ringSize = config?.ringSize ?? DEFAULT_RING_SIZE;
    this.velocityWarn = config?.velocityWarn ?? DEFAULT_VELOCITY_WARN;
    this.velocityAlert = config?.velocityAlert ?? DEFAULT_VELOCITY_ALERT;
  }

  /**
   * Record a queue position snapshot for an order.
   *
   * `elapsedMs` is REQUIRED and must be session-elapsed time from the caller's
   * `sessionElapsedClock` reading — never `Date.now()`. It is required rather
   * than defaulted so a mixed-domain ring is a visible mistake at the call
   * site instead of a silent one behind an optional argument.
   */
  record(orderId: string, queuePosition: number, elapsedMs: number): void {
    let ring = this.rings.get(orderId);
    if (!ring) {
      ring = [];
      this.rings.set(orderId, ring);
    }

    ring.push({ position: queuePosition, elapsedMs });

    // Trim to ring size
    if (ring.length > this.ringSize) {
      ring.splice(0, ring.length - this.ringSize);
    }
  }

  /**
   * Get velocity for an order (contracts/sec moving toward front).
   * Uses first and last snapshot in the ring buffer.
   * Positive velocity = queue position decreasing (advancing toward fill).
   */
  getVelocity(orderId: string): VelocityResult | null {
    const ring = this.rings.get(orderId);
    if (!ring || ring.length < 2) return null;

    const first = ring[0];
    const last = ring[ring.length - 1];
    const dt = (last.elapsedMs - first.elapsedMs) / 1000; // seconds
    if (dt <= 0) return null;

    // Position decreasing = advancing toward front of queue
    const positionDelta = first.position - last.position;
    const velocity = positionDelta / dt;

    let direction: VelocityResult['direction'] = 'stable';
    if (Math.abs(velocity) > 0.1) {
      direction = velocity > 0 ? 'advancing' : 'retreating';
    }

    return { velocity, direction };
  }

  /**
   * Re-point the two CLASSIFICATION thresholds `detectSpike` reads (#233, the
   * user's velocity delivery settings).
   *
   * Deliberately narrow: it touches nothing else. Rings, sample history, the
   * mute set, the sampling cadence, `record`, `getVelocity`, `absVelocity`,
   * and `estimateFillTime` are all untouched, so changing a threshold
   * reclassifies existing history rather than reseeding it. That matters —
   * reseeding would make every tracked order a silent "first sight" again and
   * swallow a real spike already in progress.
   *
   * Values are normalized so warn <= alert regardless of arrival order;
   * `detectSpike` tests alert first, so an inverted pair would otherwise make
   * the warn tier unreachable. Non-finite values leave the field unchanged.
   */
  setThresholds(velocityWarn: number, velocityAlert: number): void {
    const warn = Number.isFinite(velocityWarn) ? velocityWarn : this.velocityWarn;
    const alert = Number.isFinite(velocityAlert) ? velocityAlert : this.velocityAlert;
    this.velocityWarn = Math.min(warn, alert);
    this.velocityAlert = Math.max(warn, alert);
  }

  /**
   * Detect if velocity exceeds configured thresholds.
   */
  detectSpike(orderId: string): VelocitySeverity {
    const result = this.getVelocity(orderId);
    if (!result) return 'normal';

    const absVelocity = Math.abs(result.velocity);
    if (absVelocity >= this.velocityAlert) return 'alert';
    if (absVelocity >= this.velocityWarn) return 'warn';
    return 'normal';
  }

  /**
   * Check all tracked orders for spikes. Returns order IDs that crossed threshold.
   * Respects mute settings.
   */
  checkAllSpikes(): Array<{
    orderId: string;
    severity: VelocitySeverity;
    velocity: VelocityResult;
  }> {
    const spikes: Array<{ orderId: string; severity: VelocitySeverity; velocity: VelocityResult }> =
      [];

    for (const orderId of this.rings.keys()) {
      if (this.muted.has(orderId)) continue;
      const severity = this.detectSpike(orderId);
      if (severity !== 'normal') {
        const velocity = this.getVelocity(orderId)!;
        spikes.push({ orderId, severity, velocity });
      }
    }

    return spikes;
  }

  /**
   * Get the full snapshot history for an order.
   */
  getHistory(orderId: string): QueueSnapshot[] {
    return this.rings.get(orderId) ?? [];
  }

  /**
   * Mute velocity alerts for a specific order.
   */
  mute(orderId: string): void {
    this.muted.add(orderId);
  }

  /**
   * Unmute velocity alerts for a specific order.
   */
  unmute(orderId: string): void {
    this.muted.delete(orderId);
  }

  /**
   * Check if an order is muted.
   */
  isMuted(orderId: string): boolean {
    return this.muted.has(orderId);
  }

  /**
   * Remove tracking for an order (e.g., after fill or cancel).
   */
  remove(orderId: string): void {
    this.rings.delete(orderId);
    this.muted.delete(orderId);
  }

  /** Drop only measured history while preserving the trader's mute. Used when
   * queue freshness is lost: recovery must begin from a fresh baseline rather
   * than comparing a new reading to pre-outage history. */
  resetHistory(orderId: string): void {
    this.rings.delete(orderId);
  }

  /** The same fresh-baseline reset for EVERY tracked order, preserving every
   * mute. Used when the elapsed clock reports a discontinuity (#850): the
   * stored stamps stop being comparable to new ones all at once, so no ring
   * survives it, while nothing authoritative — mutes, orders, fills — moves. */
  resetAllHistory(): void {
    this.rings.clear();
  }

  /**
   * Clear all tracking data.
   */
  clear(): void {
    this.rings.clear();
    this.muted.clear();
  }

  /**
   * Number of orders currently being tracked.
   */
  get trackedCount(): number {
    return this.rings.size;
  }
}

/**
 * Estimate time to fill based on queue depth and velocity.
 *
 * @param depth    - Current queue position (contracts ahead)
 * @param velocity - Queue velocity (contracts/sec, positive = advancing)
 * @returns Estimated time in ms, or null if velocity <= 0 (stale/retreating)
 */
export function estimateFillTime(depth: number, velocity: number): number | null {
  if (velocity <= 0 || depth <= 0) return null;
  return (depth / velocity) * 1000; // convert seconds to ms
}

/**
 * Format fill time estimate for display.
 *
 * @param ms - Estimated time in ms (from estimateFillTime)
 * @returns Human-readable string ("~23 min", "~5 sec", "stale")
 */
export function formatFillEta(ms: number | null): string {
  if (ms === null) return 'stale';
  const sec = ms / 1000;
  if (sec < 60) return `~${Math.round(sec)}s`;
  if (sec < 3600) return `~${Math.round(sec / 60)}m`;
  return `~${(sec / 3600).toFixed(1)}h`;
}
